import React, { FC } from 'react';
import { RootState, useSelector } from 'src/store';
import { changeEnterHTML } from 'src/utils/changeEnter';
import { getFontURL } from 'src/utils/getFontUrl';
import { trackingList } from 'src/utils/valueHTML';

const FloatingBottomSheetHTML: FC = () => {
  const bgImage = useSelector((state: RootState) => state.imageUpload.bgImage);
  const radius = useSelector((state: RootState) => state.imageUpload.radius);
  const deeplink = useSelector(
    (state: RootState) => state.imageUpload.deeplink
  );
  const text = useSelector((state: RootState) => state.editText.text);
  const fontSize = useSelector((state: RootState) => state.editText.fontSize);
  const fontWeight = useSelector(
    (state: RootState) => state.editText.fontWeight
  );
  const color = useSelector((state: RootState) => state.editText.color);
  const fontFamily = useSelector(
    (state: RootState) => state.editText.fontFamily
  );
  const textAlign = useSelector((state: RootState) => state.editText.textAlign);
  const bgOpacity = useSelector(
    (state: RootState) => state.imageUpload.bgImageBlur
  );
  const btnText = useSelector((state: RootState) => state.buttons.btnText);
  const btnFontSize = useSelector(
    (state: RootState) => state.buttons.btnFontSize
  );
  const btnFontWeight = useSelector(
    (state: RootState) => state.buttons.btnFontWeight
  );
  const btnColor = useSelector((state: RootState) => state.buttons.btnColor);
  const btnFontFamily = useSelector(
    (state: RootState) => state.buttons.btnFontFamily
  );
  const btnAgain = useSelector((state: RootState) => state.buttons.btnAgain);

  const tracking = useSelector((state: RootState) => state.trackings.tracking);
  const button1 = useSelector((state: RootState) => state.trackings.button1);
  const button2 = useSelector((state: RootState) => state.trackings.button2);

  const backgroundDimmed = bgOpacity?.bgDimmed / 100;
  const displayValue = backgroundDimmed >= 0.01 ? backgroundDimmed : 0.3;

  const titleFontURL = getFontURL(fontFamily?.title);
  const subTitleFontURL = getFontURL(fontFamily?.sub_title);
  const btnFontURL = getFontURL(btnFontFamily?.button);
  const neverShowBtnFontURL = getFontURL(btnFontFamily?.never_show_button);

  const style = `
    @font-face {
      font-family: "title";
      src: url(${titleFontURL});
    }
    @font-face {
      font-family: "subTitle";
      src: url(${subTitleFontURL});
    }
    @font-face {
      font-family: "btn";
      src: url(${btnFontURL});
    }
    @font-face {
      font-family: "neverShowBtn";
      src: url(${neverShowBtnFontURL});
    }
      /* reset */
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
      list-style: none;
      text-decoration: none;      
    }
    
    html,
    body {
      height: 100%;
      overflow: hidden;
      font-size: 16px;      
    }
    main {
      height: 100%;
      position: relative;
    }
    a {
      color: #000000;
      cursor: pointer;
    }
    button {
      background: none;
      border: none;
    }
    .root {
			width: 100%;
			height: 100%;
			position: relative;
			background: none;
			overflow: hidden;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      align-items: center;
		}
		.background {
			width: 100%;
			height: 100%;
			position: absolute;
			top: 0;
			background: rgba(0, 0, 0, ${displayValue});
		}
		.outer-wrapper {
			position: relative;
			width: 100%;
			height: auto;
			padding: 0px 20px 30px 20px;
			display: -webkit-flex;
			display: flex;
			-webkit-flex-direction: column;
			flex-direction: column;
			-webkit-justify-content: flex-end;
			justify-content: flex-end;
      align-items: center;
      -webkit-align-items: center;
		}
		.modal-wrapper {
			display: -webkit-flex;
			display: flex;
			width: 100%;		
			aspect-ratio: 4 / 3;      
			border-radius: ${radius?.background}px;
			padding: 24px 20px 0px 20px;
      background-image: url(${bgImage?.background});
			background-size: cover;
			background-position: center;
			position: relative;
		}

		.contents-box {
			width: 100%;
      height: auto;
			display: -webkit-flex;
			display: flex;
			-webkit-flex-direction: column;
			flex-direction: column;
			position: relative;
			z-index: 1;
		}
		.contents-box .title,
		.contents-box .sub-title {
			width: 100%;
			min-height: 48px;
			display: -webkit-flex;
			display: flex;
			-webkit-align-items: center;
			align-items: center;
		}
		.title p {
      width: 100%;
      text-align: ${textAlign?.title};
      color: ${color?.title};
			font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.title
        )
          ? fontFamily?.title
          : 'title'
      };
      font-size: ${fontSize?.title}rem;
      font-weight: ${fontWeight?.title};
    }
		.sub-title p {
      width: 100%;
			text-align: ${textAlign?.sub_title};
			color: ${color?.sub_title};
			font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.sub_title
        )
          ? fontFamily?.sub_title
          : 'subTitle'
      };
      font-size: ${fontSize?.sub_title}rem;
      line-height: 1.4;
      font-weight: ${fontWeight?.sub_title};
    }
		.btn-wrapper {
			width: 100%;
			height: -webkit-fit-content;
			height: fit-content;
			display: -webkit-flex;
			display: flex;
			-webkit-justify-content: space-between;
			justify-content: space-between;
			-webkit-align-items: center;
			align-items: center;
      line-height: 2.5;
		}
		.btn-inner {
			width: 100%;
			font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          btnFontFamily?.never_show_button
        )
          ? btnFontFamily?.never_show_button
          : 'neverShowBtn'
      };
      font-size: ${btnFontSize?.never_show_button}rem;
      font-weight: ${btnFontWeight?.never_show_button};
      color: ${btnColor?.never_show_button};
		}
		.btn-close {
			width: 100%;
			height: 100%;
			display: -webkit-flex;
			display: flex;
			-webkit-align-items: center;
			align-items: center;
			justify-content: flex-end;
			-webkit-justify-content: flex-end;        
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          btnFontFamily?.button
        )
          ? btnFontFamily?.button
          : 'btn'
      };
			font-size: ${btnFontSize?.button}rem;
			
			font-weight: ${btnFontWeight?.button};
			color: ${btnColor?.button};
		}
	
		.btn-close svg {        
			fill: none;
			stroke: ${btnColor?.button};
      margin-left: 0.25rem;
		}
     .outer-wrapper.ios {
       padding-bottom: 45px;
    }

     @media screen and (min-width: 391px) {
     .outer-wrapper {
      padding: 0px 0px 30px 0px;
     }
     .outer-wrapper.ios {
      padding-bottom: 45px;
    }
    .btn-wrapper,
    .modal-wrapper {
      width: 350px;        
      }     
    }
  `;
  const deeplink_list = JSON.stringify(deeplink?.background);
  const html = `    
    <div class="root" >
		<div class="background" onclick="appboyBridge.logClick('background'); appboyBridge.closeMessage();"></div>
		<div class="outer-wrapper">
			<div class="btn-wrapper">
				<div class="btn-inner">${changeEnterHTML(btnText?.never_show_button)}</div>
				<div class="btn-close">
        ${changeEnterHTML(btnText?.button)}
					<svg 
            width="${btnFontSize?.button - 0.125}rem"
            height="${btnFontSize?.button - 0.125}rem" 
            viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"
          >
						<path d="M1 0.5L15 14.5" stroke-width="2" />
						<path d="M15 0.5L1 14.5" stroke-width="2" />
					</svg>
				</div>
			</div>
			<div class="modal-wrapper">
				<div class="contents-box">
					<div class="title">
						<p>${changeEnterHTML(text?.title)}</p>
					</div>
					<div class="sub-title">
						<p>${changeEnterHTML(text?.sub_title)}</p>
					</div>
				</div>
			</div>
      <div id="deeplink" style="display:none;">
        ${deeplink_list}
      </div>
		</div>
  `;

  const tracking_list = JSON.stringify([
    tracking?.button?.[0],
    tracking?.never_show_button?.[0],
    tracking?.content?.[0]
  ]);

  const tracking_button_list = trackingList(
    'FLOATING BOTTOM SHEET',
    button1,
    button2
  );

  const js = `
   
	let content = document.getElementById("deeplink").textContent;
	let link = content.replace(/\\n|\\t/g, "").trim();
	let deeplink = JSON.parse(link);
  const userAgent = navigator.userAgent.toLowerCase();
  const isIOS = /ipad|iphone|ipod/.test(userAgent);
  const isIPad = /ipad/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  if (isIOS || isIPad) {
    const outerWrapper = document.querySelector('.outer-wrapper');
    if (outerWrapper) {
      outerWrapper.classList.add('ios');
    }
  }
	let tracking = ${tracking_list};                        
  let button1 = ${tracking_button_list[0]};
  let button2 = ${tracking_button_list[1]};

	document.querySelector(".btn-inner").addEventListener("click", function () {
		try {
			appboyBridge.logClick(tracking[1]);
			if (button1[1].indexOf(0) !== -1) {
				appboyBridge.logClick("0");
			}
			if (button2[1].indexOf(0) !== -1) {
				appboyBridge.logClick("1");
			}
			if ("${btnAgain?.type}" === "attribute") {
				appboyBridge.getUser().setCustomUserAttribute("${btnAgain?.never_show_button}", new Date());
			}
			if ("${btnAgain?.type}" === "event") {
				appboyBridge.logCustomEvent("${btnAgain?.never_show_button}");
			}
		} catch (err) {}
		appboyBridge.closeMessage();
	});

	document.querySelector(".btn-close").addEventListener("click", function () {
		try {
			appboyBridge.logClick(tracking[0]);
			if (button1[0].indexOf(0) !== -1) {
				appboyBridge.logClick("0");
			}
			if (button2[0].indexOf(0) !== -1) {
				appboyBridge.logClick("1");
			}
		} catch (err) {}
		appboyBridge.closeMessage();
	});

	document.querySelector(".modal-wrapper").addEventListener("click", function () {
		try {
			appboyBridge.logClick(tracking[2]);
			if (button1[2].indexOf(0) !== -1) {
				appboyBridge.logClick("0");
			}
			if (button2[2].indexOf(0) !== -1) {
				appboyBridge.logClick("1");
			}
			window.location = deeplink;
		} catch (err) {}
	});

  `;
  return (
    <>
      <head>
        <meta charSet="UTF-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        <meta
          httpEquiv="Cache-Control"
          content="no-cache, no-store, must-revalidate"
        />
        <meta httpEquiv="Expires" content="0" />
        <meta httpEquiv="Pragma" content="no-cache" />
        <link
          href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Archivo:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          rel="stylesheet"
          as="style"
          href="https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.min.css"
        />
        <link
          rel="stylesheet"
          type="text/css"
          href="https://cdn.jsdelivr.net/npm/normalize.css@8.0.1/normalize.css"
        />
        <style dangerouslySetInnerHTML={{ __html: style }} />
        <script
          dangerouslySetInnerHTML={{
            __html: `               
         (function () {
          function adjustFontSize() {
            const actualSize = Math.round(parseFloat(window.getComputedStyle(document.documentElement).fontSize));
            if (actualSize !== 16) {              
              const newFontSize = (16 * 16) / actualSize;
              document.documentElement.style.fontSize = newFontSize + "px";                            
            }
          }              
          // adjust font size before body load
          adjustFontSize();
        })();                           
          `
          }}
        />
      </head>
      <body dangerouslySetInnerHTML={{ __html: html }} />
      <script dangerouslySetInnerHTML={{ __html: js }} />
    </>
  );
};

export default FloatingBottomSheetHTML;
